import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge, Alert, Pagination, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { AddonOrdersResponse, AddonOrder } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import SkeletonLoader from '../../components/common/SkeletonLoader';
import './AddonOrdersPage.scss';

const AddonOrdersPage: React.FC = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ordersData, setOrdersData] = useState<AddonOrdersResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addonFilter, setAddonFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'delivered' | 'pending'>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [processing, setProcessing] = useState<number | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!eventSlug) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apiService.scanner.getAddonOrders(eventSlug, {
        search: searchQuery,
        addon: addonFilter,
        status: statusFilter,
        page: currentPage,
      });
      setOrdersData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [eventSlug, searchQuery, addonFilter, statusFilter, currentPage]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleDeliver = useCallback(async (orderId: number) => {
    if (!eventSlug || !ordersData) return;

    const previousData = ordersData;
    const deliveredBy = user?.email || user?.username || 'Unknown';

    // Optimistically update the local state
    setOrdersData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        orders: prev.orders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                delivered: true,
                delivered_by: deliveredBy,
                delivered_at: new Date().toISOString(),
              }
            : order
        ),
      };
    });

    try {
      setProcessing(orderId);
      await apiService.scanner.deliverAddon(eventSlug, orderId);
      // Success - state already updated
    } catch (err: any) {
      // Revert on error
      setOrdersData(previousData);
      setError(err.message || 'Failed to mark order as delivered');
    } finally {
      setProcessing(null);
    }
  }, [eventSlug, ordersData, user]);

  const handleUndeliver = useCallback(async (orderId: number) => {
    if (!eventSlug || !ordersData) return;

    const previousData = ordersData;

    // Optimistically update the local state
    setOrdersData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        orders: prev.orders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                delivered: false,
                delivered_by: null,
                delivered_at: null,
              }
            : order
        ),
      };
    });

    try {
      setProcessing(orderId);
      await apiService.scanner.undeliverAddon(eventSlug, orderId);
      // Success - state already updated
    } catch (err: any) {
      // Revert on error
      setOrdersData(previousData);
      setError(err.message || 'Failed to unmark order delivery');
    } finally {
      setProcessing(null);
    }
  }, [eventSlug, ordersData]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  }, []);

  const handleBackToDashboard = useCallback(() => {
    navigate('/scanner/');
  }, [navigate]);

  const paginationItems = useMemo(() => {
    if (!ordersData || ordersData.pagination.total_pages <= 1) return null;
    
    const pages = Array.from({ length: ordersData.pagination.total_pages }, (_, i) => i + 1);
    return pages.filter(
      (page) =>
        page === 1 ||
        page === ordersData.pagination.total_pages ||
        Math.abs(page - ordersData.pagination.current_page) <= 2
    );
  }, [ordersData]);

  if (loading && !ordersData) {
    return (
      <Container className="addon-orders-page py-3 scanner-page-with-nav">
        <SkeletonLoader />
      </Container>
    );
  }

  return (
    <>
      <Container className="addon-orders-page py-3 scanner-page-with-nav">
        {/* Header */}
        <div className="scanner-header-wrapper mb-4">
          <div className="scanner-header">
            <button
              className="btn btn-glass btn-icon"
              onClick={handleBackToDashboard}
              title="Back to Dashboard"
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="header-content">
              <div className="header-icon">
                <i className="fas fa-box"></i>
              </div>
              <div className="header-text">
                <h1 className="text-primary mb-2">Addon Orders</h1>
                <p className="text-muted mb-0">Manage addon order deliveries</p>
              </div>
            </div>
            <div className="btn btn-glass btn-icon invisible">
              <i className="fas fa-arrow-left"></i>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Card className="glass-card mb-3">
          <Card.Body className="p-3">
            <Form onSubmit={handleSearch}>
              <Row className="g-2">
                <Col xs={12} md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Search</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Addon name, email, phone, or token"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Addon</Form.Label>
                    <Form.Select
                      value={addonFilter}
                      onChange={(e) => {
                        setAddonFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      size="sm"
                    >
                      <option value="">All Addons</option>
                      {ordersData?.addon_names.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Status</Form.Label>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value as '' | 'delivered' | 'pending');
                        setCurrentPage(1);
                      }}
                      size="sm"
                    >
                      <option value="">All</option>
                      <option value="delivered">Delivered</option>
                      <option value="pending">Pending</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={2} className="d-flex align-items-end">
                  <Button type="submit" variant="primary" className="w-100" size="sm">
                    <i className="fas fa-search me-2"></i>Search
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* Orders List */}
        <Card className="glass-card">
          <Card.Header className="p-3">
            <Card.Title className="card-title mb-0">
              <i className="fas fa-list me-2"></i>
              Orders ({ordersData?.pagination.total_count || 0})
            </Card.Title>
          </Card.Header>
          <Card.Body className="p-3">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : ordersData?.orders.length === 0 ? (
              <div className="empty-state text-center py-5">
                <i className="fas fa-box fa-3x text-muted mb-3"></i>
                <p className="text-muted mb-0">No orders found</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Addon</th>
                        <th>Quantity</th>
                        <th>Purchaser</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Status</th>
                        <th>Delivered By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordersData?.orders.map((order) => (
                        <tr key={order.id}>
                          <td>
                            <i className="fas fa-box me-2 text-muted"></i>
                            {order.addon_name}
                          </td>
                          <td>
                            <span className="badge bg-secondary">{order.quantity}</span>
                          </td>
                          <td>{order.purchaser_name}</td>
                          <td>{order.purchaser_email}</td>
                          <td>{order.purchaser_phone}</td>
                          <td>
                            {order.delivered ? (
                              <Badge bg="success">Delivered</Badge>
                            ) : (
                              <Badge bg="warning">Pending</Badge>
                            )}
                          </td>
                          <td>
                            {order.delivered_by || '-'}
                            {order.delivered_at && (
                              <div className="small text-muted">
                                {new Date(order.delivered_at).toLocaleString()}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              {!order.delivered ? (
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => handleDeliver(order.id)}
                                  disabled={processing === order.id}
                                  className="action-btn"
                                >
                                  {processing === order.id ? (
                                    <Spinner size="sm" animation="border" />
                                  ) : (
                                    <>
                                      <i className="fas fa-check me-1"></i>Deliver
                                    </>
                                  )}
                                </Button>
                              ) : (
                                ordersData.has_undeliver_permission && (
                                  <Button
                                    size="sm"
                                    variant="warning"
                                    onClick={() => handleUndeliver(order.id)}
                                    disabled={processing === order.id}
                                    className="action-btn"
                                  >
                                    {processing === order.id ? (
                                      <Spinner size="sm" animation="border" />
                                    ) : (
                                      <>
                                        <i className="fas fa-undo me-1"></i>Undeliver
                                      </>
                                    )}
                                  </Button>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {ordersData && ordersData.pagination.total_pages > 1 && (
                  <div className="d-flex justify-content-center mt-3">
                    <Pagination size="sm">
                      <Pagination.First
                        onClick={() => setCurrentPage(1)}
                        disabled={!ordersData.pagination.has_previous}
                      />
                      <Pagination.Prev
                        onClick={() => setCurrentPage((p) => p - 1)}
                        disabled={!ordersData.pagination.has_previous}
                      />
                      {paginationItems?.map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && <Pagination.Ellipsis />}
                          <Pagination.Item
                            active={page === ordersData.pagination.current_page}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Pagination.Item>
                        </React.Fragment>
                      ))}
                      <Pagination.Next
                        onClick={() => setCurrentPage((p) => p + 1)}
                        disabled={!ordersData.pagination.has_next}
                      />
                      <Pagination.Last
                        onClick={() => setCurrentPage(ordersData.pagination.total_pages)}
                        disabled={!ordersData.pagination.has_next}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default AddonOrdersPage;

